import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from 'react-native';
import { AlertCircle, ChevronDown } from './../icons';
import { useFormPermissions } from '../../hooks/useFormPermissions';
import { colors } from '../../theme/colors';
import { webOutlineNone } from '../../theme/webOutlineNone';
import { spacing } from '../../theme/spacing';
import { DropdownModal } from './DropdownModal';

interface FormDropdownWithOptionsProps {
  label: string;
  value: string;
  options: string[];
  onSelect: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  containerStyle?: any;
  dropdownStyle?: any;
  style?: any;
}

export const FormDropdownWithOptions: React.FC<FormDropdownWithOptionsProps> = ({
  label,
  value,
  options,
  onSelect,
  placeholder,
  required = false,
  disabled = false,
  error,
  helperText,
  containerStyle,
  dropdownStyle,
  style,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { canEditRequest } = useFormPermissions();
  const hasError = Boolean(error);
  const showPlaceholder = !value && placeholder;
  
  // Determine if the dropdown should be disabled based on permissions
  const isDisabled = disabled || !canEditRequest;

  const handlePress = () => {
    if (!isDisabled) {
      setIsModalVisible(true);
    }
  };

  const handleSelect = (selectedValue: string) => {
    onSelect(selectedValue);
  };

  return (
    <View style={[styles.formGroup, containerStyle, style]} testID="dropdown-container">
      <Text style={styles.label}>
        {label}
        {required && <Text style={styles.required}> *</Text>}
      </Text>
      <TouchableOpacity
        style={[
          styles.dropdown,
          isDisabled && styles.disabledDropdown,
          hasError && styles.errorDropdown,
          dropdownStyle,
        ]}
        onPress={handlePress}
        activeOpacity={isDisabled ? 1 : 0.7}
        accessibilityLabel={`${label} dropdown`}
        accessibilityHint={`Select ${label.toLowerCase()}`}
        accessibilityRole="button"
        testID="dropdown-trigger"
        accessibilityState={{ 
          selected: Boolean(value), 
          disabled: isDisabled,
          expanded: isModalVisible,
        }}
        {...Platform.select({
          web: {
            'aria-label': `${label} dropdown`,
            'aria-required': required,
            'aria-invalid': hasError,
            'aria-describedby': error ? `${label}-error` : helperText ? `${label}-helper` : undefined,
            role: 'combobox',
            'aria-expanded': isModalVisible,
            tabIndex: isDisabled ? -1 : 0,
            onKeyDown: (e: any) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handlePress();
              }
            },
          },
        })}
      >
        <Text style={[
          styles.dropdownText,
          showPlaceholder && styles.placeholderText,
          isDisabled && styles.disabledText,
        ]}>
          {value || placeholder}
        </Text>
        <ChevronDown 
          size={12} 
          color={isDisabled ? '#ccc' : '#666'} 
          style={styles.chevron}
        />
      </TouchableOpacity>
      
      {error && (
        <View style={styles.errorRow} accessibilityRole="text" accessibilityLabel={error}>
          <AlertCircle size={14} color={colors.error} style={styles.errorRowIcon} />
          <Text
            style={styles.errorText}
            {...Platform.select({
              web: { id: `${label}-error` },
            })}
          >
            {error}
          </Text>
        </View>
      )}
      
      {helperText && !error && (
        <Text 
          style={styles.helperText}
          {...Platform.select({
            web: { id: `${label}-helper` },
          })}
        >
          {helperText}
        </Text>
      )}

      <DropdownModal
        visible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        title={label}
        options={options}
        selectedValue={value}
        onSelect={handleSelect}
        placeholder={placeholder}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: spacing.formFieldGroupGap,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 6,
  },
  required: {
    color: colors.error,
  },
  dropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#fff',
    minHeight: 40,
    ...Platform.select({
      web: {
        ...webOutlineNone,
        cursor: 'pointer',
        ':focus': {
          borderColor: '#007bff',
          boxShadow: '0 0 0 2px rgba(0, 123, 255, 0.25)',
        },
        ':hover': {
          borderColor: '#aaa',
        },
      },
    }),
  },
  disabledDropdown: {
    backgroundColor: '#f5f5f5',
    opacity: 0.6,
    ...Platform.select({
      web: {
        cursor: 'not-allowed' as any,
        ':hover': {
          borderColor: '#ddd',
        },
      },
    }),
  },
  errorDropdown: {
    borderColor: colors.error,
  },
  dropdownText: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },
  placeholderText: {
    color: '#999',
  },
  disabledText: {
    color: '#999',
  },
  chevron: {
    marginLeft: 8,
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: 4,
    gap: 6,
  },
  errorRowIcon: {
    marginTop: 1,
  },
  errorText: {
    fontSize: 12,
    color: colors.error,
    flex: 1,
  },
  helperText: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
});